import {
  DomainEnforcementUser,
  GetDomainEnforcementStatus200Response,
} from "@coeff/api-coeff-admin";
import { Button, message, Modal, Space, Spin, Table, Tag } from "antd";
import { ColumnsType } from "antd/lib/table";
import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";

import { CaretRightBlack } from "../../../components";
import { COLORS } from "../../../constants";
import { useApiContext } from "../../../utils";

const SettingsItem = styled.div`
  background: ${COLORS.black2};
  border-radius: 6px;
  cursor: pointer;
  margin-bottom: 10px;
  padding: 12px 20px 12px 12px;
  display: flex;
  align-items: center;

  &:hover {
    background: ${COLORS.black4};
  }
`;

const ItemText = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemTitle = styled.div`
  font-weight: bold;
  color: ${COLORS.black85};
`;

const ItemDescription = styled.div`
  color: ${COLORS.black45};
  font-size: 12px;
`;

const ItemStatus = styled.div`
  margin-right: 12px;
  white-space: nowrap;
`;

const ModalStatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const Muted = styled.span`
  color: ${COLORS.black45};
  font-size: 13px;
`;

const getErrorMessage = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback;

// The impact label is state-aware: while enforcement is OFF it is a prospective "Will be blocked"
// preview; once enforcement is ON the same users are actively "Blocked".
const getColumns = (enforcementOn: boolean): ColumnsType<DomainEnforcementUser> => [
  {
    title: "Email",
    dataIndex: "email",
    key: "email",
    sorter: (a, b) => a.email.localeCompare(b.email),
  },
  {
    title: "Plan",
    dataIndex: "plan_type",
    key: "plan_type",
    sorter: (a, b) => (a.plan_type ?? "").localeCompare(b.plan_type ?? ""),
    render: (planType?: string) =>
      planType ? <Tag>{planType}</Tag> : <Muted>no billing state</Muted>,
  },
  {
    title: "Enforcement impact",
    dataIndex: "would_be_blocked",
    key: "would_be_blocked",
    // Blocked users are the point of the table — surface them first by default.
    defaultSortOrder: "descend",
    sorter: (a, b) => Number(a.would_be_blocked) - Number(b.would_be_blocked),
    filters: [
      { text: "Blocked", value: true },
      { text: "Not affected", value: false },
    ],
    onFilter: (value, record) => record.would_be_blocked === value,
    render: (wouldBeBlocked: boolean) => {
      if (!wouldBeBlocked) {
        return <Tag color="green">Not affected</Tag>;
      }
      return enforcementOn ? (
        <Tag color="red">Blocked</Tag>
      ) : (
        <Tag color="orange">Will be blocked</Tag>
      );
    },
  },
];

export const DomainEnforcementSetting: React.FC<{ domain: string }> = ({ domain }) => {
  const { coeffAdminApiClient } = useApiContext();
  const [status, setStatus] = useState<GetDomainEnforcementStatus200Response | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const loadStatus = useCallback(async () => {
    if (!domain) {
      return;
    }
    setLoading(true);
    try {
      const res = await coeffAdminApiClient.getDomainEnforcementStatus({ domain });
      setStatus(res.data);
    } catch (err) {
      message.error(getErrorMessage(err, "Failed to load enforcement status"));
    } finally {
      setLoading(false);
    }
  }, [coeffAdminApiClient, domain]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // A shared-family flag applies to every domain in the family, not just the inspected one -- the
  // guardrail copy must say so. Derived from the loaded status.
  const isFamily = status?.config_source === "shared_family";
  const scope = isFamily ? `all domains in the ${domain} domain family` : domain;

  const applyToggle = async (enabled: boolean) => {
    setSubmitting(true);
    try {
      const res = await coeffAdminApiClient.setDomainEnforcement({
        set_domain_enforcement_request: { domain, enabled },
      });
      message.success(
        enabled
          ? `Enforcement enabled — ${res.data.affected_user_count} unlicensed user(s) now blocked at ${scope}`
          : `Enforcement disabled for ${scope}`
      );
      await loadStatus();
    } catch (err) {
      message.error(getErrorMessage(err, "Failed to update enforcement"));
    } finally {
      setSubmitting(false);
    }
  };

  // Mirrors the CLI's [y/N] guardrail: confirm the (family-wide) blast radius before committing.
  // Guarded on a loaded status so we never confirm against an unknown count.
  const confirmToggle = (enabled: boolean) => {
    if (!status) {
      return;
    }
    const wouldBlock = status.would_block_count;
    Modal.confirm({
      title: enabled
        ? `Block unlicensed users for ${scope}?`
        : `Disable free-domain enforcement for ${scope}?`,
      content: enabled
        ? `${wouldBlock} unlicensed user(s) at ${scope} will be blocked from creating and refreshing imports.`
        : `Unlicensed users at ${scope} will be allowed to create and refresh imports again.`,
      okText: enabled ? `Block ${wouldBlock} user(s)` : "Disable",
      okButtonProps: { danger: enabled },
      onOk: () => applyToggle(enabled),
    });
  };

  const enabled = status?.block_unlicensed_users_from_imports ?? false;

  const summary = status
    ? `${status.would_block_count} of ${status.total_users} user(s) targeted${
        status.config_source === "shared_family" ? " — inherited from domain family" : ""
      }`
    : "";

  return (
    <>
      <SettingsItem
        onClick={() => {
          setModalOpen(true);
          loadStatus();
        }}
      >
        <ItemText>
          <ItemTitle>Free-Domain Enforcement</ItemTitle>
          <ItemDescription>Block unlicensed users from imports</ItemDescription>
        </ItemText>
        <ItemStatus>
          {!status ? (
            <Spin size="small" />
          ) : enabled ? (
            <Tag color="red">On</Tag>
          ) : (
            <Tag color="green">Off</Tag>
          )}
        </ItemStatus>
        <CaretRightBlack />
      </SettingsItem>

      <Modal
        title={`Free-Domain Enforcement — ${domain}`}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={900}
      >
        <ModalStatusRow>
          {!status ? (
            <Muted>Status unavailable</Muted>
          ) : enabled ? (
            <Tag color="red">Enforcement ON</Tag>
          ) : (
            <Tag color="default">Enforcement OFF</Tag>
          )}
          {status && <Muted>{summary}</Muted>}
          <Space style={{ marginLeft: "auto" }}>
            {!status ? (
              // Never expose the toggle against an unknown blast radius (still loading or the
              // status fetch failed) -- offer a retry instead.
              <Button onClick={loadStatus} loading={loading}>
                {loading ? "Loading…" : "Retry"}
              </Button>
            ) : enabled ? (
              <Button danger onClick={() => confirmToggle(false)} loading={submitting}>
                Disable
              </Button>
            ) : (
              <Button type="primary" onClick={() => confirmToggle(true)} loading={submitting}>
                Enable enforcement
              </Button>
            )}
          </Space>
        </ModalStatusRow>
        <Table<DomainEnforcementUser>
          rowKey="user_id"
          size="small"
          columns={getColumns(enabled)}
          dataSource={status?.users ?? []}
          loading={loading}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
        />
      </Modal>
    </>
  );
};
